import { useCallback, useState, useEffect, useRef } from 'react'
import { useSendMobileOtp, useVerifyMobileOtp } from '@/user/userQueries'
import useUserStore from '@/user/userStore'
import { analytics } from '@/common/analytics/analytics'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'
import { setUser as setSentryUser } from '@sentry/react'
import { MobileVerifyFormValues } from './rules/mobileVerifySchema'

interface UseMobileVerifyLogicResult {
    otp: string
    setOtp: (otp: string) => void
    resendTimer: number
    resent: boolean
    error: string
    isVerifying: boolean
    isSuccess: boolean
    handleResend: () => void
    handleSubmit: (data: MobileVerifyFormValues) => void
}

export function useMobileVerifyLogic(): UseMobileVerifyLogicResult {
    const verifyMobileOtp = useVerifyMobileOtp()
    const resendOtp = useSendMobileOtp()
    const [otp, setOtp] = useState('')
    const [resendTimer, setResendTimer] = useState(60)
    const [resent, setResent] = useState(false)
    const [error, setError] = useState<string>('')
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const { user } = useUserStore()

    const handleSubmit = useCallback(
        (data: MobileVerifyFormValues) => {
            verifyMobileOtp.mutate(data.otp)
            if (!user) return
            setSentryUser({
                ...user,
            })
        },
        [verifyMobileOtp, user],
    )

    const handleResend = useCallback(() => {
        if (resendTimer > 0) return
        setResent(true)
        resendOtp.mutate(undefined, {
            onSuccess: (data) => {
                analytics.trackWithDefaults(ANALYTICS_EVENTS.ONBOARDING.VERIFICATION_RESENT, {
                    sendCodeAttempts: data.sendCodeAttempts,
                    verificationType: 'mobile',
                })
                const wait = typeof data?.waitTime === 'number' ? data.waitTime : 60
                setResendTimer(wait)
            },
            onError: (err: any) => {
                analytics.trackWithDefaults(ANALYTICS_EVENTS.ONBOARDING.VERIFICATION_RESENT_FAILED, {
                    ...(err?.waitTime ? { waitTime: err.waitTime } : {}),
                    verificationType: 'mobile',
                })
                if (err?.waitTime) {
                    setResendTimer(err.waitTime)
                } else {
                    setResendTimer(60)
                }
            },
            onSettled: () => {
                setTimeout(() => setResent(false), 1000)
            },
        })
    }, [resendTimer, resendOtp])

    // Auto-submit when OTP is 6 digits
    useEffect(() => {
        if (!verifyMobileOtp.isIdle) return
        if (otp.length === 6) {
            setError('')
            verifyMobileOtp.mutate(otp)
        }
    }, [otp, verifyMobileOtp])

    // Countdown timer for resend
    useEffect(() => {
        if (resendTimer === 0) {
            if (timerRef.current) clearInterval(timerRef.current)
            return
        }
        timerRef.current = setInterval(() => {
            setResendTimer((t) => {
                if (t <= 1 && timerRef.current) {
                    clearInterval(timerRef.current)
                }
                return t > 0 ? t - 1 : 0
            })
        }, 1000)
        return () => {
            if (timerRef.current) clearInterval(timerRef.current)
        }
    }, [resendTimer])

    // Handle verification success
    useEffect(() => {
        if (!verifyMobileOtp.isSuccess) return
        analytics.trackWithDefaults(ANALYTICS_EVENTS.ONBOARDING.VERIFICATION_SUCCESS, {
            verificationType: 'mobile',
        })
    }, [verifyMobileOtp.isSuccess])

    // Handle verification error
    useEffect(() => {
        if (!verifyMobileOtp.isError) return
        analytics.trackWithDefaults(ANALYTICS_EVENTS.ONBOARDING.VERIFICATION_FAILED, {
            verificationType: 'mobile',
        })
        setError('Incorrect code entered. Please check the code and try again.')
    }, [verifyMobileOtp.isError])

    return {
        otp,
        setOtp,
        resendTimer,
        resent,
        error,
        isVerifying: verifyMobileOtp.isPending,
        isSuccess: verifyMobileOtp.isSuccess,
        handleResend,
        handleSubmit,
    }
}
